/**
 * Derive a candidate's pros & cons from the underlying product scores so
 * /candidates/$slug can render the U2 trade-off balance sheet (the bet + a
 * Why-yes / Why-pause two-column view) alongside the free-form pros/cons notes.
 *
 * Signals (v1):
 *   - STRONG_SLOT       : product scores ≥ 4 on a manual criterion in a chosen slot
 *   - REINFORCEMENT     : same (product, criterion) appears in ≥ 3 slots — collapse
 *                         per-slot bullets into ONE bullet
 *   - COVERAGE_STRONG   : candidate-level coverage % ≥ 0.70 for a journey
 *   - WEAK_SLOT         : product scores ≤ 2 on a non-dealbreaker manual criterion
 *   - WEAK_REINFORCEMENT: same shape as REINFORCEMENT, weak side
 *   - COVERAGE_GAP      : candidate-level coverage % ≤ 0.40 for a journey
 *
 * Dealbreakers are NOT echoed — they already render in their own callout.
 *
 * Salience drives ranking (uses the ACTIVE preset weight, so the order tracks
 * whichever lens is selected):
 *   pro slot           = score   × weight × criticality
 *   con slot           = (5-score) × weight × criticality
 *   pro/con reinforce  = score-or-gap × weight × Σ criticality(slots)
 *   pro/con coverage   = cov-or-gap × weight × MEAN_CRITICALITY_PROXY
 *
 * Ranking: sort by salience desc, drop bullets < 0.5× top salience, cap at TOP_N.
 * Weight-0 criteria (e.g. deprioritized multi-tenant-isolation) have salience 0
 * and never surface.
 */

import { architectureComponents } from "@/presentation/data/architecture-components";
import type {
  CandidateAggregateInputs,
  CandidateAggregateResult,
  ComponentAggregate,
} from "./compute-candidate-aggregates";
import {
  strategicForCoverage,
  strategicForCriterion,
  type StrategicLine,
} from "./strategic-insights";
import type { ProductScoreRow } from "@/infrastructure/supabase/database.types";

const STRONG_THRESHOLD = 4;
const WEAK_THRESHOLD = 2;
const REINFORCEMENT_MIN_SLOTS = 3;
const COVERAGE_STRONG = 0.7;
const COVERAGE_GAP = 0.4;
const MEAN_CRITICALITY_PROXY = 15;
const NOISE_FLOOR = 0.5;
// The min-3 backfill won't pad with items below this fraction of the top
// salience — keeps a contradictory near-noise bullet (e.g. a 1-slot product)
// from being shown just to reach the minimum count. Below it, we show fewer.
const BACKFILL_FLOOR = 0.2;
const TOP_N = 6;
const MIN_KEEP = 3;

export type DerivedSignal =
  | "STRONG_SLOT"
  | "REINFORCEMENT"
  | "COVERAGE_STRONG"
  | "WEAK_SLOT"
  | "WEAK_REINFORCEMENT"
  | "COVERAGE_GAP";

export interface DerivedBullet {
  id: string;
  sign: "pro" | "con";
  signal: DerivedSignal;
  /** CEO-facing headline + consequence. Null = no template for this criterion. */
  strategicImpact: StrategicLine | null;
  /** Technical / mechanical evidence (drill-down). Minimal **bold** markdown. */
  text: string;
  /** Meta-row fields for the balance-sheet chips. */
  criterionLabel: string;
  scoreLabel: string;
  weightPct: number;
  isDealbreakerEligible: boolean;
  salience: number;
  productId: string | null;
  criterionId: string | null;
  componentIds: string[];
}

export interface DerivedProsCons {
  pros: DerivedBullet[];
  cons: DerivedBullet[];
}

export function deriveCandidateProsCons(
  inputs: CandidateAggregateInputs,
  aggregate: CandidateAggregateResult
): DerivedProsCons {
  const productById = new Map(inputs.products.map((p) => [p.id, p]));
  const componentNameById = new Map(architectureComponents.map((c) => [c.id, c.name]));

  // Index live, non-dealbreaker, non-TBD slots per product
  const slotsByProduct = new Map<string, ComponentAggregate[]>();
  for (const ca of aggregate.componentAggregates) {
    if (ca.isTBD || ca.isDealbreakerFail || !ca.productId) continue;
    const arr = slotsByProduct.get(ca.productId) ?? [];
    arr.push(ca);
    slotsByProduct.set(ca.productId, arr);
  }

  // Index scores by (product, criterion)
  const scoreByKey = new Map<string, ProductScoreRow>();
  for (const s of inputs.productScores) {
    scoreByKey.set(`${s.product_id}__${s.criterion_id}`, s);
  }

  const bullets: DerivedBullet[] = [];

  // --- SLOT-level signals (pro + con) ---
  for (const [productId, slots] of slotsByProduct) {
    const product = productById.get(productId);
    if (!product) continue;
    const totalCriticality = slots.reduce((acc, sl) => acc + sl.criticality, 0);
    const meanCriticality = totalCriticality / slots.length;
    const isReinforcement = slots.length >= REINFORCEMENT_MIN_SLOTS;

    for (const c of inputs.criteria) {
      if (c.derivation_kind === "derived-coverage") continue;
      const s = scoreByKey.get(`${productId}__${c.id}`);
      if (!s || s.score === null || s.dealbreaker_fail) continue;
      const w = inputs.weights[c.slug] ?? c.weight_default;
      if (w <= 0) continue; // weight-0 criteria never surface
      const score = s.score;
      const meta = {
        criterionLabel: c.name,
        scoreLabel: `${score}/5`,
        weightPct: Math.round(w * 100),
        isDealbreakerEligible: c.is_dealbreaker_eligible,
      };

      if (score >= STRONG_THRESHOLD) {
        const strategic = strategicForCriterion(c.slug, "pro");
        if (isReinforcement) {
          bullets.push({
            id: `pro-rein-${productId}-${c.id}`,
            sign: "pro",
            signal: "REINFORCEMENT",
            strategicImpact: strategic,
            text: reinforcementText(
              "pro",
              product.name,
              c.name,
              score,
              w,
              slots,
              componentNameById,
              meanCriticality
            ),
            ...meta,
            salience: score * w * totalCriticality,
            productId,
            criterionId: c.id,
            componentIds: slots.map((sl) => sl.componentId),
          });
        } else {
          for (const slot of slots) {
            bullets.push({
              id: `pro-slot-${productId}-${c.id}-${slot.componentId}`,
              sign: "pro",
              signal: "STRONG_SLOT",
              strategicImpact: strategic,
              text: slotText(
                "pro",
                product.name,
                c.name,
                score,
                w,
                componentNameById.get(slot.componentId) ?? slot.componentId,
                slot.criticality
              ),
              ...meta,
              salience: score * w * slot.criticality,
              productId,
              criterionId: c.id,
              componentIds: [slot.componentId],
            });
          }
        }
      }

      if (score <= WEAK_THRESHOLD) {
        const strategic = strategicForCriterion(c.slug, "con");
        if (isReinforcement) {
          bullets.push({
            id: `con-rein-${productId}-${c.id}`,
            sign: "con",
            signal: "WEAK_REINFORCEMENT",
            strategicImpact: strategic,
            text: reinforcementText(
              "con",
              product.name,
              c.name,
              score,
              w,
              slots,
              componentNameById,
              meanCriticality
            ),
            ...meta,
            salience: (5 - score) * w * totalCriticality,
            productId,
            criterionId: c.id,
            componentIds: slots.map((sl) => sl.componentId),
          });
        } else {
          for (const slot of slots) {
            bullets.push({
              id: `con-slot-${productId}-${c.id}-${slot.componentId}`,
              sign: "con",
              signal: "WEAK_SLOT",
              strategicImpact: strategic,
              text: slotText(
                "con",
                product.name,
                c.name,
                score,
                w,
                componentNameById.get(slot.componentId) ?? slot.componentId,
                slot.criticality
              ),
              ...meta,
              salience: (5 - score) * w * slot.criticality,
              productId,
              criterionId: c.id,
              componentIds: [slot.componentId],
            });
          }
        }
      }
    }
  }

  // --- COVERAGE signals ---
  const coverageEntries: {
    journey: keyof CandidateAggregateResult["coverage"];
    label: string;
    slug: string;
  }[] = [
    { journey: "b2c", label: "B2C", slug: "b2c-feature-coverage" },
    { journey: "b2bMvp", label: "B2B MVP", slug: "non-mvp-b2b-feature-coverage" },
    { journey: "b2bNonMvp", label: "B2B non-MVP", slug: "non-mvp-b2b-feature-coverage" },
  ];
  for (const entry of coverageEntries) {
    const breakdown = aggregate.coverage[entry.journey];
    if (breakdown === null) continue;
    const value = breakdown.totalRatio;
    const crit = inputs.criteria.find((c) => c.slug === entry.slug);
    const w = inputs.weights[entry.slug] ?? crit?.weight_default ?? 0.06;
    if (w <= 0) continue;
    const meta = {
      criterionLabel: `${entry.label} coverage`,
      scoreLabel: `${Math.round(value * 100)}%`,
      weightPct: Math.round(w * 100),
      isDealbreakerEligible: crit?.is_dealbreaker_eligible ?? false,
    };
    if (value >= COVERAGE_STRONG) {
      bullets.push({
        id: `pro-cov-${entry.journey}`,
        sign: "pro",
        signal: "COVERAGE_STRONG",
        strategicImpact: strategicForCoverage(entry.slug, "pro"),
        text: `**${entry.label} coverage ${Math.round(value * 100)}%** — chosen stack lands native-or-plugin on most in-scope ${entry.label} features.`,
        ...meta,
        salience: value * w * MEAN_CRITICALITY_PROXY,
        productId: null,
        criterionId: crit?.id ?? null,
        componentIds: [],
      });
    } else if (value <= COVERAGE_GAP) {
      bullets.push({
        id: `con-cov-${entry.journey}`,
        sign: "con",
        signal: "COVERAGE_GAP",
        strategicImpact: strategicForCoverage(entry.slug, "con"),
        text: `**${entry.label} coverage ${Math.round(value * 100)}%** — most of the in-scope ${entry.label} feature set still needs custom build or vendor work.`,
        ...meta,
        salience: (1 - value) * w * MEAN_CRITICALITY_PROXY,
        productId: null,
        criterionId: crit?.id ?? null,
        componentIds: [],
      });
    }
  }

  return {
    pros: rank(bullets.filter((b) => b.sign === "pro")),
    cons: rank(bullets.filter((b) => b.sign === "con")),
  };
}

function reinforcementText(
  sign: "pro" | "con",
  product: string,
  criterion: string,
  score: number,
  weight: number,
  slots: ComponentAggregate[],
  componentName: Map<string, string>,
  meanCriticality: number
): string {
  const componentList = slots
    .slice(0, 3)
    .map((sl) => componentName.get(sl.componentId) ?? sl.componentId)
    .join(", ");
  const more = slots.length > 3 ? `, +${slots.length - 3} more` : "";
  return `**${criterion} (${score}/5)** — ${product} fills ${slots.length} slots in this candidate (${componentList}${more}), so this ${sign === "pro" ? "strength" : "weakness"} compounds · weight ${pct(weight)}, mean criticality ${meanCriticality.toFixed(1)}/5`;
}

function slotText(
  sign: "pro" | "con",
  product: string,
  criterion: string,
  score: number,
  weight: number,
  componentName: string,
  criticality: number
): string {
  const verb = sign === "pro" ? "rated" : "rated only";
  return `**${criterion} (${score}/5)** — ${product} ${verb} on this criterion · in the ${componentName} slot · weight ${pct(weight)}, criticality ${criticality}/5`;
}

function pct(w: number): string {
  return `${Math.round(w * 100)}%`;
}

/** The line the user reads — criterion-level, so per-product bullets collide here. */
function headlineKey(b: DerivedBullet): string {
  return b.strategicImpact?.headline ?? b.criterionLabel;
}

/**
 * Collapse bullets that render the same headline (multiple products weak/strong
 * on the same criterion) into ONE bullet: salience sums (a weakness across many
 * products is more salient), the products' evidence is concatenated, and the
 * score chip becomes "N products".
 */
function mergeByHeadline(bullets: DerivedBullet[]): DerivedBullet[] {
  const groups = new Map<string, DerivedBullet[]>();
  for (const b of bullets) {
    const key = headlineKey(b);
    const arr = groups.get(key) ?? [];
    arr.push(b);
    groups.set(key, arr);
  }
  const out: DerivedBullet[] = [];
  for (const group of groups.values()) {
    if (group.length === 1) {
      out.push(group[0]!);
      continue;
    }
    const rep = [...group].sort((a, b) => b.salience - a.salience)[0]!;
    const productBased = group.every((b) => b.productId !== null);
    out.push({
      ...rep,
      salience: group.reduce((s, b) => s + b.salience, 0),
      componentIds: [...new Set(group.flatMap((b) => b.componentIds))],
      scoreLabel: productBased ? `${group.length} products` : rep.scoreLabel,
      text: group.map((b) => b.text).join("\n"),
    });
  }
  return out;
}

function rank(bullets: DerivedBullet[]): DerivedBullet[] {
  const merged = mergeByHeadline(bullets);
  if (merged.length === 0) return [];
  // Always keep items above the noise floor. Backfill toward MIN_KEEP only with
  // items that clear the (lower) backfill floor — never pad with near-noise.
  const sorted = [...merged].sort((a, b) => b.salience - a.salience);
  const top = sorted[0]!.salience;
  const passes = sorted.filter(
    (b, i) =>
      b.salience >= NOISE_FLOOR * top || (i < MIN_KEEP && b.salience >= BACKFILL_FLOOR * top)
  );
  return passes.slice(0, TOP_N);
}
